import type { Parent, Root, Text } from "mdast";
import type { Plugin } from "unified";
import { SKIP, visit } from "unist-util-visit";

/**
 * Remark plugin to convert "break" nodes (hard line breaks) to newline text.
 *
 * In markdown, `  \n` (two trailing spaces + newline) creates a hard break (<br>).
 * TipTap serializes soft breaks (shift+enter) using this syntax.
 *
 * When CSS `white-space: pre-line` is used, the newline character alone creates
 * a visual line break. Combined with the <br> from markdown, this causes double spacing.
 *
 * This plugin replaces break nodes with newline text, letting CSS handle line breaks.
 *
 * Without this plugin: `  \n` → <br> + CSS newline = double spacing
 * With this plugin: break nodes → "\n" text → CSS renders single line break
 */
export const remarkStripSoftBreaks: Plugin<[], Root> = () => {
  return (tree: Root) => {
    visit(tree, "break", (_node, index, parent) => {
      if (parent && index !== undefined) {
        // Replace the break node with a newline text node
        const newlineNode: Text = { type: "text", value: "\n" };
        (parent as Parent).children.splice(index, 1, newlineNode);
        // Skip past the replacement node
        return [SKIP, index + 1];
      }
    });
  };
};
